import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { Navigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ShoppingBag, Plus, Edit2, Trash2, ExternalLink, Package, DollarSign, Link as LinkIcon, Image, Check, Loader2, ClipboardList, Mail, Phone, MapPin, Calendar, CreditCard, Hammer, Truck, PackageCheck, X, Hash, Upload, GripVertical, ArrowUp, ArrowDown, Save, Clock, CheckCircle2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';

import { API_URL } from '../utils/api';

const AdminMerchandisePage = () => {
  const { user, token } = useAuth();
  const [merchandise, setMerchandise] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ordersLoading, setOrdersLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('products');
  const [updatingOrderId, setUpdatingOrderId] = useState(null);
  const [reorderMode, setReorderMode] = useState(false);
  const [reorderedItems, setReorderedItems] = useState([]);
  const [savingOrder, setSavingOrder] = useState(false);
  const [orderNumberInputs, setOrderNumberInputs] = useState({});
  const [uploading, setUploading] = useState(false);
  const [uploadingIndex, setUploadingIndex] = useState(null);
  const fileInputRef = useRef(null);
  const [draggedImageIndex, setDraggedImageIndex] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    sale_price: '',
    stripe_checkout_link: '',
    images: [], // Array of image URLs (first one is main)
    image_url: '', // Keep for backwards compatibility
    available: true,
    on_sale: false,
    featured: false,
    show_size_warning: false
  });

  // Handle image upload
  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    // Validate file size (max 15MB)
    if (file.size > 15 * 1024 * 1024) {
      toast.error('Image too large. Max 15MB allowed.');
      return;
    }

    // Check max 4 images
    if (formData.images.length >= 4) {
      toast.error('Maximum 4 images allowed');
      return;
    }

    setUploading(true);
    const uploadFormData = new FormData();
    uploadFormData.append('file', file);

    try {
      const res = await axios.post(`${API_URL}/api/upload`, uploadFormData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      if (res.data.url) {
        const newImages = [...formData.images, res.data.url];
        setFormData({ 
          ...formData, 
          images: newImages,
          image_url: newImages[0] // Main image is always first
        });
        toast.success('Image uploaded successfully');
      }
    } catch (err) {
      console.error('Upload failed:', err);
      toast.error('Failed to upload image');
    } finally {
      setUploading(false);
      // Clear the file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Remove an image
  const removeImage = (index) => {
    const newImages = formData.images.filter((_, i) => i !== index);
    setFormData({
      ...formData,
      images: newImages,
      image_url: newImages[0] || '' // Update main image
    });
  };

  // Drag and drop handlers for images
  const handleImageDragStart = (e, index) => {
    setDraggedImageIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleImageDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleImageDrop = (e, dropIndex) => {
    e.preventDefault();
    if (draggedImageIndex === null || draggedImageIndex === dropIndex) return;

    const newImages = [...formData.images];
    const draggedImage = newImages[draggedImageIndex];
    newImages.splice(draggedImageIndex, 1);
    newImages.splice(dropIndex, 0, draggedImage);

    setFormData({
      ...formData,
      images: newImages,
      image_url: newImages[0] // First image is always main
    });
    setDraggedImageIndex(null);
    toast.success('Image order updated. First image is the main photo.');
  };

  const handleImageDragEnd = () => {
    setDraggedImageIndex(null);
  };

  // Check if admin
  const isAdmin = user?.email === 'ebonnie_faith@hotmail.com' || user?.email === 'craftersmarketaus@gmail.com' || user?.is_admin;

  useEffect(() => {
    if (isAdmin && token) {
      fetchMerchandise();
      fetchOrders();
    }
  }, [isAdmin, token]);

  const fetchMerchandise = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/merchandise`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMerchandise(res.data.items || []);
    } catch (err) {
      console.error('Failed to fetch merchandise', err);
      toast.error('Failed to load merchandise');
    } finally {
      setLoading(false);
    }
  };

  const fetchOrders = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/merchandise/orders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const ordersData = res.data.orders || [];
      setOrders(ordersData);
      // Initialize order number inputs
      const inputs = {};
      ordersData.forEach(order => {
        inputs[order.id] = order.order_number || '';
      });
      setOrderNumberInputs(inputs);
    } catch (err) {
      console.error('Failed to fetch orders', err);
    } finally {
      setOrdersLoading(false);
    }
  };

  const updateOrderStatus = async (orderId, newStatus) => {
    setUpdatingOrderId(orderId);
    try {
      const res = await axios.put(
        `${API_URL}/api/admin/merchandise/orders/${orderId}/status?status=${newStatus}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (res.data.email_sent) {
        toast.success(`Status updated to "${newStatus}" - Customer notified by email! 📧`);
      } else {
        toast.success(`Status updated to "${newStatus}"`);
      }
      fetchOrders();
    } catch (err) {
      console.error('Failed to update order status', err);
      toast.error('Failed to update status');
    } finally {
      setUpdatingOrderId(null);
    }
  };

  const saveOrderNumber = async (orderId) => {
    const orderNumber = orderNumberInputs[orderId];
    if (!orderNumber?.trim()) {
      toast.error('Please enter an order number');
      return;
    }
    
    try {
      await axios.put(
        `${API_URL}/api/admin/merchandise/orders/${orderId}/order-number?order_number=${encodeURIComponent(orderNumber)}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Order number saved!');
      fetchOrders();
    } catch (err) {
      console.error('Failed to save order number', err);
      toast.error('Failed to save order number');
    }
  };

  const deleteOrder = async (orderId) => {
    try {
      await axios.delete(
        `${API_URL}/api/admin/merchandise/orders/${orderId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Order deleted');
      fetchOrders();
    } catch (err) {
      console.error('Failed to delete order', err);
      toast.error('Failed to delete order');
    }
  };

  // Filter orders into new (pending/paid) and completed (filled/shipped/delivered)
  const newOrders = orders.filter(o => ['pending', 'paid'].includes(o.status));
  const completedOrders = orders.filter(o => ['filled', 'shipped', 'delivered', 'cancelled'].includes(o.status));

  // Reorder functions
  const enterReorderMode = () => {
    setReorderedItems([...merchandise]);
    setReorderMode(true);
  };

  const exitReorderMode = () => {
    setReorderMode(false);
    setReorderedItems([]);
  };

  const moveItem = (index, direction) => {
    const newItems = [...reorderedItems];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    
    if (newIndex < 0 || newIndex >= newItems.length) return;
    
    [newItems[index], newItems[newIndex]] = [newItems[newIndex], newItems[index]];
    setReorderedItems(newItems);
  };

  const saveNewOrder = async () => {
    setSavingOrder(true);
    try {
      const orderData = reorderedItems.map((item, index) => ({
        id: item.id,
        sort_order: index
      }));
      
      await axios.put(
        `${API_URL}/api/admin/merchandise/reorder`,
        { items: orderData },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setMerchandise(reorderedItems);
      toast.success('Product order saved!');
      exitReorderMode();
    } catch (err) {
      console.error('Failed to save order', err);
      toast.error('Failed to save product order');
    } finally {
      setSavingOrder(false);
    }
  };

  const openCreateModal = () => {
    setEditingItem(null);
    setFormData({
      name: '',
      description: '',
      price: '',
      sale_price: '',
      stripe_checkout_link: '',
      images: [],
      image_url: '',
      available: true,
      on_sale: false,
      featured: false,
      show_size_warning: false
    });
    setModalOpen(true);
  };

  const openEditModal = (item) => {
    setEditingItem(item);
    // Convert old single image to array if needed
    const images = item.images || (item.image_url ? [item.image_url] : []);
    setFormData({
      name: item.name,
      description: item.description,
      price: item.price.toString(),
      sale_price: item.sale_price ? item.sale_price.toString() : '',
      stripe_checkout_link: item.stripe_checkout_link,
      images: images,
      image_url: images[0] || '',
      available: item.available,
      on_sale: item.on_sale || false,
      featured: item.featured || false,
      show_size_warning: item.show_size_warning || false
    });
    setModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.price || !formData.stripe_checkout_link) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        name: formData.name,
        description: formData.description,
        price: parseFloat(formData.price),
        sale_price: formData.on_sale && formData.sale_price ? parseFloat(formData.sale_price) : null,
        stripe_checkout_link: formData.stripe_checkout_link,
        images: formData.images, // Array of images
        image_url: formData.images[0] || null, // Main image (first in array)
        available: formData.available,
        on_sale: formData.on_sale,
        featured: formData.featured,
        show_size_warning: formData.show_size_warning
      };

      if (editingItem) {
        await axios.put(
          `${API_URL}/api/admin/merchandise/${editingItem.id}`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Merchandise updated successfully');
      } else {
        await axios.post(
          `${API_URL}/api/admin/merchandise`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Merchandise created successfully');
      }

      setModalOpen(false);
      fetchMerchandise();
    } catch (err) {
      console.error('Failed to save merchandise', err);
      toast.error(err.response?.data?.detail || 'Failed to save merchandise');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (item) => {
    try {
      await axios.delete(`${API_URL}/api/admin/merchandise/${item.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Merchandise deleted');
      fetchMerchandise();
    } catch (err) {
      console.error('Failed to delete merchandise', err);
      toast.error('Failed to delete merchandise');
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD'
    }).format(price);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-AU', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-700 border-yellow-200',
      paid: 'bg-blue-100 text-blue-700 border-blue-200',
      filled: 'bg-purple-100 text-purple-700 border-purple-200',
      shipped: 'bg-green-100 text-green-700 border-green-200',
      delivered: 'bg-emerald-100 text-emerald-700 border-emerald-200',
      cancelled: 'bg-teal-100 text-teal-600 border-teal-200'
    };
    return styles[status] || styles.pending;
  };

  const getNextAction = (status) => {
    // Return the next action button based on current status
    switch (status) {
      case 'pending':
        return { label: 'Mark as Paid', status: 'paid', icon: CreditCard, color: 'bg-blue-600 hover:bg-blue-700' };
      case 'paid':
        return { label: 'Mark as Filled', status: 'filled', icon: Hammer, color: 'bg-purple-600 hover:bg-purple-700', emailNote: 'Customer will be notified' };
      case 'filled':
        return { label: 'Mark as Shipped', status: 'shipped', icon: Truck, color: 'bg-green-600 hover:bg-green-700', emailNote: 'Customer will be notified' };
      case 'shipped':
        return { label: 'Mark as Delivered', status: 'delivered', icon: PackageCheck, color: 'bg-emerald-600 hover:bg-emerald-700', emailNote: 'Customer will be notified' };
      default:
        return null;
    }
  };

  if (!user) {
    return <Navigate to="/login" />;
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-[#FAFAF9] flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-stone-900">Access Denied</h1>
          <p className="text-stone-500 mt-2">You don&apos;t have permission to view this page.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-stone-900 flex items-center gap-3" style={{ fontFamily: 'Playfair Display, serif' }}>
              <ShoppingBag className="h-8 w-8 text-teal-600" />
              Merchandise Store
            </h1>
            <p className="text-stone-500 mt-1">Manage products and orders</p>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="mb-6 flex-wrap">
            <TabsTrigger value="products" className="flex items-center gap-2">
              <Package className="h-4 w-4" />
              Products ({merchandise.length})
            </TabsTrigger>
            <TabsTrigger value="new-orders" className="flex items-center gap-2">
              <Clock className="h-4 w-4" />
              New Orders ({newOrders.length})
            </TabsTrigger>
            <TabsTrigger value="completed-orders" className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4" />
              Completed ({completedOrders.length})
            </TabsTrigger>
          </TabsList>

          {/* Products Tab */}
          <TabsContent value="products">
            <div className="flex justify-between items-center mb-6">
              <div>
                {reorderMode && (
                  <p className="text-sm text-amber-600 font-medium">
                    Use arrows to reorder products, then save
                  </p>
                )}
              </div>
              <div className="flex gap-2">
                {reorderMode ? (
                  <>
                    <Button 
                      variant="outline"
                      onClick={exitReorderMode}
                    >
                      Cancel
                    </Button>
                    <Button 
                      onClick={saveNewOrder}
                      disabled={savingOrder}
                      className="bg-green-600 hover:bg-green-700 text-white"
                    >
                      {savingOrder ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Save className="h-4 w-4 mr-2" />
                      )}
                      Save Order
                    </Button>
                  </>
                ) : (
                  <>
                    {merchandise.length > 1 && (
                      <Button 
                        variant="outline"
                        onClick={enterReorderMode}
                        data-testid="reorder-btn"
                      >
                        <GripVertical className="h-4 w-4 mr-2" />
                        Reorganise
                      </Button>
                    )}
                    <Button 
                      onClick={openCreateModal}
                      className="bg-gradient-to-r from-sky-500 to-teal-500 hover:from-sky-600 hover:to-teal-600 text-white"
                      data-testid="add-merchandise-btn"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add Product
                    </Button>
                  </>
                )}
              </div>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-16">
                <div className="animate-spin h-8 w-8 border-2 border-stone-300 border-t-teal-600 rounded-full" />
              </div>
            ) : merchandise.length === 0 ? (
              <div className="bg-white border border-stone-200 rounded-lg p-12 text-center">
                <Package className="h-16 w-16 text-stone-300 mx-auto mb-4" />
                <h2 className="text-xl font-semibold text-stone-900 mb-2">No merchandise yet</h2>
                <p className="text-stone-500 mb-6">Add your first product to get started</p>
                <Button onClick={openCreateModal} className="bg-gradient-to-r from-sky-500 to-teal-500 hover:from-sky-600 hover:to-teal-600 text-white">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Your First Product
                </Button>
              </div>
            ) : reorderMode ? (
              /* Reorder Mode View */
              <div className="bg-white border border-stone-200 rounded-lg overflow-hidden">
                <div className="bg-amber-50 border-b border-amber-200 px-4 py-3">
                  <p className="text-sm text-amber-800 font-medium">
                    📦 Drag products to reorder how they appear in the store
                  </p>
                </div>
                <div className="divide-y divide-stone-100">
                  {reorderedItems.map((item, index) => (
                    <div 
                      key={item.id}
                      className="flex items-center gap-4 p-4 hover:bg-stone-50"
                    >
                      <div className="flex flex-col gap-1">
                        <button
                          onClick={() => moveItem(index, 'up')}
                          disabled={index === 0}
                          className={`p-1 rounded ${index === 0 ? 'text-stone-300 cursor-not-allowed' : 'text-stone-500 hover:bg-stone-200 hover:text-stone-700'}`}
                        >
                          <ArrowUp className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => moveItem(index, 'down')}
                          disabled={index === reorderedItems.length - 1}
                          className={`p-1 rounded ${index === reorderedItems.length - 1 ? 'text-stone-300 cursor-not-allowed' : 'text-stone-500 hover:bg-stone-200 hover:text-stone-700'}`}
                        >
                          <ArrowDown className="h-4 w-4" />
                        </button>
                      </div>
                      
                      <span className="w-8 h-8 flex items-center justify-center bg-stone-100 rounded-full text-sm font-medium text-stone-600">
                        {index + 1}
                      </span>
                      
                      {item.image_url ? (
                        <img 
                          src={item.image_url} 
                          alt={item.name}
                          className="w-16 h-16 object-cover rounded-lg"
                        />
                      ) : (
                        <div className="w-16 h-16 bg-stone-100 rounded-lg flex items-center justify-center">
                          <Package className="h-6 w-6 text-stone-400" />
                        </div>
                      )}
                      
                      <div className="flex-1">
                        <h3 className="font-semibold text-stone-900">{item.name}</h3>
                        <p className="text-sm text-stone-500">{formatPrice(item.price)}</p>
                      </div>
                      
                      <div className="flex gap-1">
                        {item.featured && (
                          <span className="text-xs bg-amber-100 text-amber-700 px-2 py-1 rounded">Featured</span>
                        )}
                        {item.on_sale && (
                          <span className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded">Sale</span>
                        )}
                        {!item.available && (
                          <span className="text-xs bg-stone-100 text-stone-600 px-2 py-1 rounded">Hidden</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {merchandise.map((item) => (
                  <div 
                    key={item.id} 
                    className={`bg-white border border-stone-200 rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow ${!item.available ? 'opacity-60' : ''}`}
                    data-testid={`merchandise-item-${item.id}`}
                  >
                    {item.image_url ? (
                      <div className="aspect-square bg-stone-100">
                        <img 
                          src={item.image_url} 
                          alt={item.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    ) : (
                      <div className="aspect-square bg-gradient-to-br from-stone-100 to-stone-200 flex items-center justify-center">
                        <Package className="h-16 w-16 text-stone-400" />
                      </div>
                    )}

                    <div className="p-4">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <h3 className="font-semibold text-stone-900 text-lg">{item.name}</h3>
                          <p className="text-2xl font-bold text-stone-900 mt-1">{formatPrice(item.price)}</p>
                        </div>
                        <div className="flex flex-col gap-1 items-end">
                          {!item.available && (
                            <span className="text-xs bg-stone-200 text-stone-600 px-2 py-1 rounded">
                              Unavailable
                            </span>
                          )}
                          {item.featured && (
                            <span className="text-xs bg-amber-500 text-white px-2 py-1 rounded">
                              ⭐ Featured
                            </span>
                          )}
                          {item.on_sale && (
                            <span className="text-xs bg-red-500 text-white px-2 py-1 rounded">
                              ON SALE
                            </span>
                          )}
                        </div>
                      </div>
                      
                      {item.description && (
                        <p className="text-stone-500 text-sm mt-2 line-clamp-2">{item.description}</p>
                      )}

                      <a 
                        href={item.stripe_checkout_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-3 text-xs text-blue-600 hover:text-blue-700 flex items-center gap-1 truncate"
                      >
                        <ExternalLink className="h-3 w-3 flex-shrink-0" />
                        <span className="truncate">{item.stripe_checkout_link}</span>
                      </a>

                      <div className="flex items-center gap-2 mt-4 pt-4 border-t border-stone-100">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openEditModal(item)}
                          className="flex-1"
                        >
                          <Edit2 className="h-4 w-4 mr-1" />
                          Edit
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-teal-600 hover:text-teal-600 hover:bg-teal-50"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete Product</AlertDialogTitle>
                              <AlertDialogDescription>
                                Are you sure you want to delete &quot;{item.name}&quot;? This action cannot be undone.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => handleDelete(item)}
                                className="bg-gradient-to-r from-sky-500 to-teal-500 hover:from-sky-600 hover:to-teal-600"
                              >
                                Delete
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* New Orders Tab */}
          <TabsContent value="new-orders">
            {ordersLoading ? (
              <div className="flex items-center justify-center py-16">
                <div className="animate-spin h-8 w-8 border-2 border-stone-300 border-t-teal-600 rounded-full" />
              </div>
            ) : newOrders.length === 0 ? (
              <div className="bg-white border border-stone-200 rounded-lg p-12 text-center">
                <Clock className="h-16 w-16 text-stone-300 mx-auto mb-4" />
                <h2 className="text-xl font-semibold text-stone-900 mb-2">No new orders</h2>
                <p className="text-stone-500">New orders will appear here when customers make purchases</p>
              </div>
            ) : (
              <div className="space-y-4">
                {newOrders.map((order) => {
                  const isUpdating = updatingOrderId === order.id;
                  
                  return (
                    <div 
                      key={order.id}
                      className="bg-white border border-stone-200 rounded-lg overflow-hidden"
                      data-testid={`order-${order.id}`}
                    >
                      {/* Status Bar */}
                      <div className={`px-6 py-3 border-b ${getStatusBadge(order.status)} border-l-4`}>
                        <div className="flex items-center justify-between">
                          <span className="font-semibold capitalize">
                            {order.status === 'paid' ? '💳 Paid - Ready to Process' : '⏳ Pending Payment'}
                          </span>
                          <span className="text-sm opacity-75">{formatDate(order.created_at)}</span>
                        </div>
                      </div>
                      
                      <div className="p-6">
                        <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-6">
                          {/* Order Info */}
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-4">
                              <h3 className="font-semibold text-xl text-stone-900">{order.item_name}</h3>
                              <span className="text-2xl font-bold text-stone-900">{formatPrice(order.item_price)}</span>
                            </div>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                              {/* Customer Details */}
                              <div className="space-y-2">
                                <p className="text-sm font-medium text-stone-700 flex items-center gap-1">
                                  <Mail className="h-4 w-4" />
                                  Customer
                                </p>
                                <div className="bg-stone-50 p-3 rounded-lg space-y-1">
                                  <p className="font-medium text-stone-900">{order.customer?.full_name}</p>
                                  <p className="text-sm text-stone-600">{order.customer?.email}</p>
                                  {order.customer?.phone && (
                                    <p className="text-sm text-stone-600 flex items-center gap-1">
                                      <Phone className="h-3 w-3" />
                                      {order.customer?.phone}
                                    </p>
                                  )}
                                </div>
                              </div>
                              
                              {/* Shipping Address */}
                              <div className="space-y-2">
                                <p className="text-sm font-medium text-stone-700 flex items-center gap-1">
                                  <MapPin className="h-4 w-4" />
                                  Shipping Address
                                </p>
                                <div className="bg-stone-50 p-3 rounded-lg text-sm text-stone-600">
                                  <p>{order.shipping_address?.address_line1}</p>
                                  {order.shipping_address?.address_line2 && (
                                    <p>{order.shipping_address?.address_line2}</p>
                                  )}
                                  <p>
                                    {order.shipping_address?.city}, {order.shipping_address?.state} {order.shipping_address?.postcode}
                                  </p>
                                  <p className="font-medium">{order.shipping_address?.country}</p>
                                </div>
                              </div>
                            </div>
                            
                            {order.size && (
                              <div className="mt-4 p-3 bg-purple-50 border border-purple-200 rounded-lg">
                                <p className="text-sm text-purple-800">
                                  <span className="font-medium">📏 Size:</span> {order.size}
                                </p>
                              </div>
                            )}
                          </div>
                          
                          {/* Action Buttons */}
                          <div className="flex flex-col gap-3 min-w-[200px]">
                            {order.status === 'paid' && (
                              <Button
                                onClick={() => updateOrderStatus(order.id, 'filled')}
                                disabled={isUpdating}
                                className="w-full bg-amber-600 hover:bg-amber-700 text-white"
                              >
                                {isUpdating ? (
                                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                ) : (
                                  <Hammer className="h-4 w-4 mr-2" />
                                )}
                                Mark as Processed
                              </Button>
                            )}
                            
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button
                                  variant="outline"
                                  className="w-full text-red-600 hover:text-red-600 hover:bg-red-50"
                                >
                                  <Trash2 className="h-4 w-4 mr-2" />
                                  Delete Order
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Delete Order</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    Are you sure you want to delete this order? Use this for test orders or mistakes. This cannot be undone.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Keep Order</AlertDialogCancel>
                                  <AlertDialogAction
                                    onClick={() => deleteOrder(order.id)}
                                    className="bg-red-600 hover:bg-red-700"
                                  >
                                    Delete Order
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </TabsContent>

          {/* Completed Orders Tab */}
          <TabsContent value="completed-orders">
            {ordersLoading ? (
              <div className="flex items-center justify-center py-16">
                <div className="animate-spin h-8 w-8 border-2 border-stone-300 border-t-teal-600 rounded-full" />
              </div>
            ) : completedOrders.length === 0 ? (
              <div className="bg-white border border-stone-200 rounded-lg p-12 text-center">
                <CheckCircle2 className="h-16 w-16 text-stone-300 mx-auto mb-4" />
                <h2 className="text-xl font-semibold text-stone-900 mb-2">No completed orders yet</h2>
                <p className="text-stone-500">Orders will move here after you mark them as processed</p>
              </div>
            ) : (
              <div className="space-y-4">
                {completedOrders.map((order) => {
                  const nextAction = getNextAction(order.status);
                  const isUpdating = updatingOrderId === order.id;
                  
                  return (
                    <div 
                      key={order.id}
                      className="bg-white border border-stone-200 rounded-lg overflow-hidden"
                      data-testid={`order-${order.id}`}
                    >
                      {/* Status Bar */}
                      <div className={`px-6 py-3 border-b ${getStatusBadge(order.status)} border-l-4`}>
                        <div className="flex items-center justify-between">
                          <span className="font-semibold capitalize">
                            {order.status === 'filled' ? '🔨 Being Made' : 
                             order.status === 'shipped' ? '🚚 Shipped' :
                             order.status === 'delivered' ? '✅ Delivered' :
                             order.status === 'cancelled' ? '❌ Cancelled' :
                             '⏳ Processing'}
                          </span>
                          <span className="text-sm opacity-75">{formatDate(order.created_at)}</span>
                        </div>
                      </div>
                      
                      <div className="p-6">
                        <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-6">
                          {/* Order Info */}
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-4">
                              <h3 className="font-semibold text-xl text-stone-900">{order.item_name}</h3>
                              <span className="text-2xl font-bold text-stone-900">{formatPrice(order.item_price)}</span>
                            </div>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                              {/* Customer Details */}
                              <div className="space-y-2">
                                <p className="text-sm font-medium text-stone-700 flex items-center gap-1">
                                  <Mail className="h-4 w-4" />
                                  Customer
                                </p>
                                <div className="bg-stone-50 p-3 rounded-lg space-y-1">
                                  <p className="font-medium text-stone-900">{order.customer?.full_name}</p>
                                  <p className="text-sm text-stone-600">{order.customer?.email}</p>
                                  {order.customer?.phone && (
                                    <p className="text-sm text-stone-600 flex items-center gap-1">
                                      <Phone className="h-3 w-3" />
                                      {order.customer?.phone}
                                    </p>
                                  )}
                                </div>
                              </div>
                              
                              {/* Shipping Address */}
                              <div className="space-y-2">
                                <p className="text-sm font-medium text-stone-700 flex items-center gap-1">
                                  <MapPin className="h-4 w-4" />
                                  Shipping Address
                                </p>
                                <div className="bg-stone-50 p-3 rounded-lg text-sm text-stone-600">
                                  <p>{order.shipping_address?.address_line1}</p>
                                  {order.shipping_address?.address_line2 && (
                                    <p>{order.shipping_address?.address_line2}</p>
                                  )}
                                  <p>
                                    {order.shipping_address?.city}, {order.shipping_address?.state} {order.shipping_address?.postcode}
                                  </p>
                                  <p className="font-medium">{order.shipping_address?.country}</p>
                                </div>
                              </div>
                            </div>
                            
                            {order.size && (
                              <div className="mt-4 p-3 bg-purple-50 border border-purple-200 rounded-lg">
                                <p className="text-sm text-purple-800">
                                  <span className="font-medium">📏 Size:</span> {order.size}
                                </p>
                              </div>
                            )}
                            
                            {/* Order Number Input */}
                            <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                              <Label className="text-sm font-medium text-blue-800 flex items-center gap-1 mb-2">
                                <Hash className="h-4 w-4" />
                                Third-Party Order Number
                              </Label>
                              <div className="flex gap-2">
                                <Input
                                  value={orderNumberInputs[order.id] || ''}
                                  onChange={(e) => setOrderNumberInputs({
                                    ...orderNumberInputs,
                                    [order.id]: e.target.value
                                  })}
                                  placeholder="Enter order number..."
                                  className="bg-white"
                                />
                                <Button
                                  size="sm"
                                  onClick={() => saveOrderNumber(order.id)}
                                  className="bg-blue-600 hover:bg-blue-700 text-white"
                                >
                                  Save
                                </Button>
                              </div>
                              {order.order_number && (
                                <p className="text-xs text-blue-600 mt-1">
                                  ✓ Saved: {order.order_number}
                                </p>
                              )}
                            </div>
                          </div>
                          
                          {/* Action Buttons */}
                          <div className="flex flex-col gap-3 min-w-[200px]">
                            {order.status !== 'delivered' && order.status !== 'cancelled' && nextAction && (
                              <div className="space-y-2">
                                <Button
                                  onClick={() => updateOrderStatus(order.id, nextAction.status)}
                                  disabled={isUpdating}
                                  className={`w-full ${nextAction.color} text-white`}
                                >
                                  {isUpdating ? (
                                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                  ) : (
                                    <nextAction.icon className="h-4 w-4 mr-2" />
                                  )}
                                  {nextAction.label}
                                </Button>
                                {nextAction.emailNote && (
                                  <p className="text-xs text-center text-stone-500">
                                    📧 {nextAction.emailNote}
                                  </p>
                                )}
                              </div>
                            )}
                            
                            {order.status !== 'cancelled' && order.status !== 'delivered' && (
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <Button
                                    variant="outline"
                                    className="w-full text-orange-600 hover:text-orange-600 hover:bg-orange-50"
                                  >
                                    <X className="h-4 w-4 mr-2" />
                                    Cancel Order
                                  </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>Cancel Order</AlertDialogTitle>
                                    <AlertDialogDescription>
                                      Are you sure you want to cancel this order? The customer will be notified.
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>Keep Order</AlertDialogCancel>
                                    <AlertDialogAction
                                      onClick={() => updateOrderStatus(order.id, 'cancelled')}
                                      className="bg-orange-600 hover:bg-orange-700"
                                    >
                                      Cancel Order
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            )}
                            
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button
                                  variant="outline"
                                  className="w-full text-red-600 hover:text-red-600 hover:bg-red-50"
                                >
                                  <Trash2 className="h-4 w-4 mr-2" />
                                  Delete Order
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Delete Order</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    Are you sure you want to delete this order? Use this for test orders or mistakes. This cannot be undone.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Keep Order</AlertDialogCancel>
                                  <AlertDialogAction
                                    onClick={() => deleteOrder(order.id)}
                                    className="bg-red-600 hover:bg-red-700"
                                  >
                                    Delete Order
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                            
                            {order.status === 'delivered' && (
                              <div className="text-center p-4 bg-emerald-50 rounded-lg">
                                <PackageCheck className="h-8 w-8 text-emerald-600 mx-auto mb-2" />
                                <p className="text-sm font-medium text-emerald-700">Order Complete!</p>
                              </div>
                            )}
                            
                            {order.status === 'cancelled' && (
                              <div className="text-center p-4 bg-gray-50 rounded-lg">
                                <X className="h-8 w-8 text-gray-500 mx-auto mb-2" />
                                <p className="text-sm font-medium text-gray-600">Order Cancelled</p>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Create/Edit Modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {editingItem ? (
                <>
                  <Edit2 className="h-5 w-5 text-teal-600" />
                  Edit Product
                </>
              ) : (
                <>
                  <Plus className="h-5 w-5 text-teal-600" />
                  Add New Product
                </>
              )}
            </DialogTitle>
            <DialogDescription>
              {editingItem 
                ? 'Update the product details below' 
                : 'Fill in the product details. Customers will be redirected to Stripe to complete their purchase.'
              }
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="name" className="flex items-center gap-1">
                <Package className="h-4 w-4" />
                Product Name *
              </Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g. Crafters Market Hoodie"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Describe your product..."
                rows={3}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="price" className="flex items-center gap-1">
                <DollarSign className="h-4 w-4" />
                {formData.on_sale ? 'Original Price (AUD) *' : 'Price (AUD) *'}
              </Label>
              <Input
                id="price"
                type="number"
                step="0.01"
                min="0"
                value={formData.price}
                onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                placeholder="49.00"
                required
              />
            </div>

            {formData.on_sale && (
              <div className="space-y-2">
                <Label htmlFor="sale_price" className="flex items-center gap-1 text-red-600">
                  <DollarSign className="h-4 w-4" />
                  Sale Price (AUD) *
                </Label>
                <Input
                  id="sale_price"
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.sale_price}
                  onChange={(e) => setFormData({ ...formData, sale_price: e.target.value })}
                  placeholder="39.00"
                  className="border-red-300 focus:border-red-500"
                  required
                />
                {formData.price && formData.sale_price && parseFloat(formData.sale_price) < parseFloat(formData.price) && (
                  <p className="text-xs text-green-600 font-medium">
                    💰 Customers save ${(parseFloat(formData.price) - parseFloat(formData.sale_price)).toFixed(2)} ({Math.round((1 - parseFloat(formData.sale_price) / parseFloat(formData.price)) * 100)}% off)
                  </p>
                )}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="stripe_checkout_link" className="flex items-center gap-1">
                <LinkIcon className="h-4 w-4" />
                Stripe Checkout Link *
              </Label>
              <Input
                id="stripe_checkout_link"
                type="url"
                value={formData.stripe_checkout_link}
                onChange={(e) => setFormData({ ...formData, stripe_checkout_link: e.target.value })}
                placeholder="https://buy.stripe.com/..."
                required
              />
              <p className="text-xs text-stone-500">
                Create a payment link in your Stripe Dashboard and paste it here
              </p>
            </div>

            <div className="space-y-2">
              <Label className="flex items-center gap-1">
                <Image className="h-4 w-4" />
                Product Images ({formData.images.length}/4)
              </Label>
              <p className="text-xs text-stone-500">
                Upload up to 4 images. Drag to reorder - first image is the main photo.
              </p>
              
              {/* Hidden file input */}
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleImageUpload}
                accept="image/*"
                className="hidden"
              />
              
              {/* Image gallery with drag and drop */}
              {formData.images.length > 0 && (
                <div className="flex flex-wrap gap-3 mb-3">
                  {formData.images.map((img, index) => (
                    <div
                      key={index}
                      draggable
                      onDragStart={(e) => handleImageDragStart(e, index)}
                      onDragOver={(e) => handleImageDragOver(e, index)}
                      onDrop={(e) => handleImageDrop(e, index)}
                      onDragEnd={handleImageDragEnd}
                      className={`relative w-24 h-24 rounded-lg overflow-hidden bg-stone-100 border-2 cursor-move transition-all ${
                        index === 0 ? 'border-teal-500 ring-2 ring-teal-200' : 'border-stone-200'
                      } ${draggedImageIndex === index ? 'opacity-50 scale-95' : ''}`}
                    >
                      <img 
                        src={img} 
                        alt={`Preview ${index + 1}`}
                        className="w-full h-full object-cover"
                        onError={(e) => e.target.style.display = 'none'}
                      />
                      {/* Main badge */}
                      {index === 0 && (
                        <div className="absolute top-1 left-1 bg-teal-500 text-white text-xs px-1.5 py-0.5 rounded font-medium">
                          Main
                        </div>
                      )}
                      {/* Drag handle indicator */}
                      <div className="absolute top-1 right-1 bg-black/50 text-white p-1 rounded">
                        <GripVertical className="h-3 w-3" />
                      </div>
                      {/* Remove button */}
                      <button
                        type="button"
                        onClick={() => removeImage(index)}
                        className="absolute bottom-1 right-1 bg-red-500 text-white p-1 rounded hover:bg-red-600 transition-colors"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Upload button */}
              {formData.images.length < 4 && (
                <div 
                  onClick={() => !uploading && fileInputRef.current?.click()}
                  className={`border-2 border-dashed border-stone-300 rounded-lg p-4 text-center cursor-pointer hover:border-teal-400 hover:bg-teal-50/50 transition-colors ${uploading ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  {uploading ? (
                    <>
                      <Loader2 className="h-6 w-6 mx-auto text-teal-500 animate-spin mb-1" />
                      <p className="text-sm text-stone-600">Uploading...</p>
                    </>
                  ) : (
                    <>
                      <Upload className="h-6 w-6 mx-auto text-stone-400 mb-1" />
                      <p className="text-sm text-stone-600">
                        {formData.images.length === 0 ? 'Click to upload images' : 'Add another image'}
                      </p>
                      <p className="text-xs text-stone-400 mt-1">PNG, JPG up to 15MB</p>
                    </>
                  )}
                </div>
              )}
            </div>

            <div className="flex items-center justify-between py-2">
              <div>
                <Label htmlFor="available">Available for Purchase</Label>
                <p className="text-xs text-stone-500">Turn off to hide from customers</p>
              </div>
              <Switch
                id="available"
                checked={formData.available}
                onCheckedChange={(checked) => setFormData({ ...formData, available: checked })}
              />
            </div>

            <div className="flex items-center justify-between py-2 border-t border-stone-100 pt-4">
              <div>
                <Label htmlFor="on_sale" className="flex items-center gap-2">
                  <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded">SALE</span>
                  On Sale
                </Label>
                <p className="text-xs text-stone-500">Show "ON SALE" badge to customers</p>
              </div>
              <Switch
                id="on_sale"
                checked={formData.on_sale}
                onCheckedChange={(checked) => setFormData({ ...formData, on_sale: checked })}
              />
            </div>

            <div className="flex items-center justify-between py-2">
              <div>
                <Label htmlFor="featured" className="flex items-center gap-2">
                  <span className="bg-amber-500 text-white text-xs px-2 py-0.5 rounded">⭐ FEATURED</span>
                  Featured Item
                </Label>
                <p className="text-xs text-stone-500">Highlight this product at the top</p>
              </div>
              <Switch
                id="featured"
                checked={formData.featured}
                onCheckedChange={(checked) => setFormData({ ...formData, featured: checked })}
              />
            </div>

            <div className="flex items-center justify-between py-2 border-t border-stone-100 mt-2 pt-4">
              <div>
                <Label htmlFor="show_size_warning" className="flex items-center gap-2">
                  <span className="bg-orange-500 text-white text-xs px-2 py-0.5 rounded">📏 SIZE</span>
                  Show Size Warning
                </Label>
                <p className="text-xs text-stone-500">Display "Check size chart - no returns" notice</p>
              </div>
              <Switch
                id="show_size_warning"
                checked={formData.show_size_warning}
                onCheckedChange={(checked) => setFormData({ ...formData, show_size_warning: checked })}
              />
            </div>

            <DialogFooter className="pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setModalOpen(false)}
                disabled={saving}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-gradient-to-r from-sky-500 to-teal-500 hover:from-sky-600 hover:to-teal-600 text-white"
                disabled={saving}
              >
                {saving ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Check className="h-4 w-4 mr-2" />
                    {editingItem ? 'Update Product' : 'Create Product'}
                  </>
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminMerchandisePage;
